import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address")
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: ""
    },
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsSubmitting(true);
    try {
      const response = await apiRequest("POST", "/auth/forgot-password", data);
      const result = await response.json();
      
      if (response.ok) {
        toast({
          title: "Reset Email Sent",
          description: "If an account with that email exists, you will receive password reset instructions.",
        });
        setLocation("/login");
      } else {
        throw new Error(result.message || "Failed to send reset email");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 font-muli">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 relative">
        <CardHeader className="text-center space-y-2 pt-16">
          {/* Back Button */}
          <div className="absolute top-4 left-4">
            <Button
              variant="ghost"
              onClick={() => setLocation("/login")}
              className="text-servify-purple hover:text-servify-purple-dark hover:bg-servify-purple/10"
            >
              ← Back to Login
            </Button>
          </div>
          <CardTitle className="text-3xl font-bold text-servify-purple">
            Forgot Password
          </CardTitle>
          <CardDescription className="text-gray-600">
            Enter your email address and we'll send you a link to reset your password
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium">Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter your email address"
                        className="h-12 border-gray-300 focus:border-servify-purple focus:ring-servify-purple/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-servify-purple hover:bg-servify-purple-dark text-white font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {isSubmitting ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Remember your password?{" "}
              <button 
                onClick={() => setLocation("/login")}
                className="text-servify-purple hover:text-servify-purple-dark font-medium underline hover:no-underline transition-colors"
              >
                Back to Login
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
